// @ts-check

import { CustomizerDrillComponent } from '@theme/custom.customizer-drill';
import { ThemeEvents, CustomizerConfirmRemoveEvent } from '@theme/events';
import { repricedChain } from '@theme/custom.customizer-store';
import { track } from '@theme/custom.customizer-analytics';

/** @typedef {import('@theme/custom.customizer-store').CustomizerStore} CustomizerStore */
/** @typedef {import('@theme/custom.customizer-store').ChainSelection} ChainSelection */
/** @typedef {import('@theme/events').CustomizerUpdateEvent} CustomizerUpdateEvent */

/**
 * One chain length (size) option. Only SELLABLE variants arrive here: the snippet reads the
 * published add-on chain collection and skips anything unavailable or SKU-less, so everything in
 * the graph is sellable at its shown price.
 * @typedef {Object} ChainLength
 * @property {string} key - The chain variant's SKU: the stable id the Cart Transform resolves
 *   through `custom.customizer_components.chains` to a `{ gid, cents }` entry. `priceCents` below
 *   is that entry's cents, which the daily sync copies from the variant's own price, the price the
 *   chain line bills once it is nested under the design.
 * @property {string} label - The size label (e.g. `16"`, `Adjustable 16-20”`).
 * @property {string} metal - Full metal name shown after the size in the row label (e.g. "Sterling
 *   Silver"); `""` for a metal-agnostic length.
 * @property {string} tone - The metal's base color (`white-gold`, `yellow-gold`, `rose-gold`),
 *   matched against the selected locket's `store.selectedMetalColor`. The snippet reads it from the
 *   store's metal colors; a metal in none is `""` = metal-agnostic (always shown, never filtered).
 * @property {string} [sku] - The same SKU as `key`, kept as its own field because `_config.chain`
 *   records both: `key` is what the function resolves, `sku` is order identity for the bench.
 * @property {number} [variantId] - The chain variant's id: what the theme adds as the design's own
 *   nested chain line (assets/custom.customizer-components.js). Never a price or a pricing key.
 * @property {string | null} image - This row's own picture: the variant's, else its product's
 *   featured image, which is the one photograph of this metal (the snippet picks). Drives the
 *   size-view hero swap, so each row shows its own metal, not the card's single image.
 * @property {number} priceCents - The chain's full price for this metal+size, in minor units: the
 *   allowlist entry's cents, the same number the nested chain line bills.
 * @property {string | null} priceLabel - Pre-formatted full price (null when unpriced).
 * @property {boolean} adjustable
 * @property {string | null} badge - Merchandising badge (e.g. "Most Popular"); empty/null when
 *   none.
 */

/**
 * One chain style (the products sharing a style tag), owning its image and nested lengths. Carries
 * no price of its own: the whole chain price lives on each {@link ChainLength}.
 * @typedef {Object} ChainOption
 * @property {string} title
 * @property {string} description
 * @property {string | null} image
 * @property {boolean} adjustable
 * @property {ChainLength[]} lengths
 */

/**
 * One chain category, the audience/gender level (women, men, kids, from product tags).
 * @typedef {Object} ChainCategory
 * @property {string} title
 * @property {number} position - The fixed audience-vocabulary rank the snippet emits; the picker
 *   orders categories by it ascending, defaulting a missing rank to 999999 (sorted last).
 * @property {string | null} image
 * @property {string | null} subtitle - "Multiple styles from N and up.", server-emitted only when
 *   the audience has >1 style (null otherwise); N is this audience's shortest fixed size.
 * @property {number} fromPriceCents - The cheapest sellable length price in this category, in
 *   minor units (full-price-on-the-row; only rendered, sellable variants count).
 * @property {string | null} fromPriceLabel - Pre-formatted "From" price (null when no priced
 *   option).
 * @property {ChainOption[]} options
 */

/**
 * Chain picker inside the customizer `chain` step. Reads the add-on chain collection's products,
 * server-emitted by snippets/custom.customizer-chain.liquid as a `[data-customizer-chains]` JSON
 * graph, and renders a three-level drill: category (audience), option (style), length (size+metal).
 * Each length carries a metal + tone; the picker filters lengths to the base color of the selected
 * locket variant's metal (`store.selectedMetalColor`) so only tone-matching chains show, and
 * re-filters when the shopper changes the locket metal. A locket tone an audience stocks no chain
 * in filters nothing there, and a style with no size left to pick is not offered.
 *
 * Built on {@link CustomizerDrillComponent}, so the wizard-integration contract (`data-drilled`
 * Back routing, the shared breadcrumb header cloned from the option-head template in the snippet,
 * the drill broadcast the step chrome re-renders on, the deferred viewport repaint) all comes from
 * the base; this class only owns the chain data and its three views. Its option rows/cards carry
 * `data-option-card`, so the fleet selected treatment applies with no bespoke CSS.
 *
 * Picking a length commits the full selection (incl. metal) to the host `<custom-customizer>` store
 * via `store.setChain(...)`. The length's price is the whole chain price: folded into the footer
 * total, and recorded in `_config.chain` for the Cart Transform to bill from. The step is optional,
 * and a "Skip" affordance on the first view advances past it (the host clears any partial
 * selection).
 *
 * Only the active view's nodes are ever in the DOM. `on:` bindings on the rendered elements are
 * handled by the global delegation in component.js, so dynamically-created nodes need no wiring.
 *
 * @extends {CustomizerDrillComponent}
 */
class CustomizerChain extends CustomizerDrillComponent {
  /** Parsed category graph (empty until the add-on chain collection has published, available
   * products, since unpublished chains are not sold). @type {ChainCategory[]} */
  #categories = [];

  /** Every sellable chain's billed price, keyed by SKU (the emitted
   * `[data-customizer-chain-prices]` blob, cents from the allowlist entries). Empty when nothing
   * is published: then nothing is sellable. @type {Record<string, unknown>} */
  #priceTable = {};

  /** Active category index (-1 in the category list). @type {number} */
  #categoryIndex = -1;

  /** Active option index within the category (-1 until a style is opened). @type {number} */
  #optionIndex = -1;

  /** The committed selection mirrored from the store, or null. @type {ChainSelection | null} */
  #selection = null;

  /** The locket metal tone last seen from the store, so `#onStoreUpdate` can detect a tone change
   * and re-filter/reconcile. @type {string | null} */
  #lastTone = null;

  /** Cached skip-icon markup (trusted: inlined from theme assets by the snippet).
   * @type {string} */
  #skipIcon = '';

  /** The tones the shop actually stocks a chain in, read once off the emitted graph; a locket tone
   * outside this set filters nothing. @type {Set<string>} */
  #stockedTones = new Set();

  /** The same per audience, by its title: an audience with no chain in the locket's tone filters
   * nothing, so another audience's rose chains cannot empty its list.
   * @type {Map<string, Set<string>>} */
  #audienceTones = new Map();

  /** Each length's audience tones, so the length filter can ask without its category in hand.
   * @type {WeakMap<ChainLength, Set<string>>} */
  #lengthTones = new WeakMap();

  /** Each chain metal's tone as the graph carries it, for a committed pick that holds only its
   * metal. @type {Map<string, string>} */
  #metalTones = new Map();

  get rootView() {
    return 'categories';
  }

  connectedCallback() {
    super.connectedCallback();
    this.#categories = this.#parse();
    this.#stockedTones = this.#indexTones();
    this.#priceTable = this.#parsePrices();
    this.#reportGroupScope();
    this.#reportAudienceLimit();
    this.#skipIcon = this.querySelector('template[data-chain-icon="skip"]')?.innerHTML ?? '';
    this.#selection = this.#store?.chain ?? null;
    this.#lastTone = this.#tone;
    // A hydrate landing later is caught by the broadcast handler below.
    this.#repriceRestoredChain();
    document.addEventListener(ThemeEvents.customizerUpdate, this.#onStoreUpdate);
    document.addEventListener(ThemeEvents.customizerRemoveConfirmed, this.#onRemoveConfirmed);
    this.refreshView();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#onStoreUpdate);
    document.removeEventListener(ThemeEvents.customizerRemoveConfirmed, this.#onRemoveConfirmed);
  }

  /** @returns {(HTMLElement & { store?: CustomizerStore }) | null} The host `<custom-customizer>`. */
  get #host() {
    return /** @type {(HTMLElement & { store?: CustomizerStore }) | null} */ (
      this.closest('custom-customizer')
    );
  }

  /** @returns {CustomizerStore | undefined} */
  get #store() {
    return this.#host?.store;
  }

  /** @returns {string} */
  get #configId() {
    return this.#host?.dataset.configId ?? '';
  }

  /** @returns {string | null} The locket's base metal color; chains filter to it. */
  get #tone() {
    return this.#store?.selectedMetalColor ?? null;
  }

  /**
   * Indexes the tones the shop stocks, once off the emitted graph, because they describe the shop's
   * chain data and not the locket in front of the shopper: each audience's own tones and each
   * length's audience, which the length filter, the stale-chain drop and the entry hero read.
   * @returns {Set<string>} every tone some sellable length carries
   */
  #indexTones() {
    const tones = new Set();
    this.#audienceTones = new Map();
    this.#lengthTones = new WeakMap();
    this.#metalTones = new Map();
    for (const category of this.#categories) {
      // Two audiences under one title share a set, which errs toward filtering and dropping.
      /** @type {Set<string>} */
      const audience = this.#audienceTones.get(category.title) ?? new Set();
      for (const option of category.options) {
        for (const length of option.lengths) {
          if (length.tone) audience.add(length.tone);
          if (length.tone && length.metal) this.#metalTones.set(length.metal, length.tone);
          this.#lengthTones.set(length, audience);
        }
      }
      this.#audienceTones.set(category.title, audience);
      for (const tone of audience) tones.add(tone);
    }
    return tones;
  }

  /**
   * Whether a length should show given the selected locket tone. With no locket tone yet (metal not
   * chosen / unresolvable) or for a metal-agnostic length (blank `tone`), everything shows;
   * otherwise only tone-matching lengths do.
   *
   * A tone NO chain in the length's audience is stocked in filters nothing, for the same reason an
   * unclaimed chain group widens instead of emptying (custom.customizer-chain.liquid): an audience
   * with no chain in the locket's tone would otherwise lose every style and drop out of the list.
   * @param {ChainLength} length
   * @returns {boolean}
   */
  #lengthVisible(length) {
    const tone = this.#tone;
    // Every length the graph emits is indexed, so each has its audience's tones.
    const stocked = /** @type {Set<string>} */ (this.#lengthTones.get(length));
    if (!tone || !length.tone || !stocked.has(tone)) return true;
    return length.tone === tone;
  }

  /**
   * Whether a style has a size the shopper can pick. A style whose every length is filtered out
   * draws a card that drills into an empty size list, so it is not offered at all.
   * @param {ChainOption} option
   * @returns {boolean}
   */
  #optionVisible(option) {
    return option.lengths.some((length) => this.#lengthVisible(length));
  }

  /**
   * The picture that stands for a style before any row is picked.
   *
   * A style card spans every metal but its server-side donor is the first selling product's, so on
   * a silver locket it can be the gold shot. Prefer a row the tone filter leaves, which is a chain
   * the shopper can actually buy in the finish they are building.
   * @param {ChainOption} option
   * @returns {string | null}
   */
  #optionImage(option) {
    const inTone = option.lengths.find((length) => this.#lengthVisible(length) && length.image);
    return inTone?.image ?? option.image;
  }

  /**
   * The tone-aware "From" price label for a category: the cheapest VISIBLE length price in the
   * audience given the locket tone, using that length's server-formatted label. Falls back to the
   * server-computed `fromPriceLabel` (the un-filtered cheapest) when nothing prices out.
   * @param {ChainCategory} category
   * @returns {string | null}
   */
  #categoryFromLabel(category) {
    let cents = Infinity;
    /** @type {string | null} */
    let label = null;
    for (const option of category.options) {
      for (const length of option.lengths) {
        if (!this.#lengthVisible(length)) continue;
        if (length.priceCents > 0 && length.priceCents < cents) {
          cents = length.priceCents;
          label = length.priceLabel;
        }
      }
    }
    return label ?? category.fromPriceLabel;
  }

  /**
   * Reconcile the highlighted selection when the store changes from OUTSIDE this component, chiefly
   * a Skip that clears the chain. This component's own picks update `#selection` before calling
   * `setChain`, so the echoing update is a no-op (no re-render loop). A cleared selection resets
   * the drill to the category list so returning to the step starts fresh.
   * @param {Event} event
   */
  #onStoreUpdate = (event) => {
    const { configId } = /** @type {CustomizerUpdateEvent} */ (event).detail;
    if (configId !== this.#configId) return;

    // A locket metal change shifts the tone, which re-filters the chains and can strand a committed
    // pick - so react to tone changes too, not only chain-selection changes.
    const tone = this.#tone;
    const toneChanged = tone !== this.#lastTone;
    this.#lastTone = tone;

    const next = this.#store?.chain ?? null;
    const selectionChanged = !this.#sameSelection(next, this.#selection);
    if (!selectionChanged && !toneChanged) return;
    this.#selection = next;

    // A committed chain in another metal is dropped so it cannot ride the order, unless its metal
    // is blank or its audience stocks nothing in this tone: then it is still on offer.
    const stocked = this.#audienceTones.get(next?.category ?? '') ?? this.#stockedTones;
    if (toneChanged && next && tone && stocked.has(tone)) {
      const selectedTone = this.#metalTones.get(next.metal ?? '');
      if (selectedTone && selectedTone !== tone) {
        this.#store?.setChain(null);
        return;
      }
    }

    if (!next) {
      this.#categoryIndex = -1;
      this.#optionIndex = -1;
      this.toRoot();
      return;
    }

    // A restored chain arrives priced from the saved line, not from the table checkout bills. Its
    // correction echoes back as a no-op, so this paint is the only one either branch gets.
    if (this.#repriceRestoredChain() && !this.#selection) {
      this.#categoryIndex = -1;
      this.#optionIndex = -1;
      this.toRoot();
      return;
    }

    this.refreshView();
  };

  /**
   * The live cents for a chain SKU, or null when no sellable collection variant carries it. Read
   * from the whole blob rather than the rendered rows: those are narrowed by audience and by the
   * document's `only` list, and a SKU outside them is still one checkout honours. A degenerate row
   * (null, a string, a negative) reads as absent too - the graph skips it and checkout refuses it.
   * @param {string} key
   * @returns {number | null}
   */
  #livePriceFor(key) {
    const cents = this.#priceTable[key];
    return typeof cents === 'number' && Number.isFinite(cents) && cents >= 0 ? cents : null;
  }

  /**
   * Settle a chain this picker did not commit (only `hydrate` produces one) against the live rows,
   * so an edit cannot show the price recorded at add-to-cart while checkout bills today's.
   * @returns {boolean} True when the store was corrected, so the caller can stop and let the
   *   resulting broadcast repaint.
   */
  #repriceRestoredChain() {
    const store = this.#store;
    const { chain: next, changed } = repricedChain(store?.chain ?? null, (key) =>
      this.#livePriceFor(key)
    );
    if (!changed) return false;
    if (!next) {
      console.info(
        `[customizer] restored chain "${store?.chain?.key ?? ''}" is no longer sellable from the add-on chain collection - dropped from the line rather than shown at a price checkout will not honour.`
      );
    }
    // Mirror BEFORE committing, the same discipline as `pickLength`: the echoed store update then
    // no-ops in `#onStoreUpdate` instead of re-entering this reconcile.
    this.#selection = next;
    store?.setChain(next);
    return true;
  }

  /**
   * @param {ChainSelection | null} a
   * @param {ChainSelection | null} b
   * @returns {boolean}
   */
  #sameSelection(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    // Metals share size labels (16" Sterling Silver vs 16" Stainless Steel), so the label alone is
    // ambiguous and metal is part of identity.
    return (
      a.category === b.category &&
      a.option === b.option &&
      a.length === b.length &&
      (a.metal ?? '') === (b.metal ?? '')
    );
  }

  /**
   * Parse the server-emitted JSON blob and rank categories by `position` ascending (the fixed
   * audience-vocabulary order the snippet emits). A missing rank defaults to 999999 (sorted
   * last); the sort is stable. Never throws.
   * @returns {ChainCategory[]}
   */
  #parse() {
    const script = this.querySelector('script[data-customizer-chains]');
    if (!script?.textContent) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      if (!Array.isArray(parsed)) return [];
      return parsed.sort((a, b) => (a.position ?? 999999) - (b.position ?? 999999));
    } catch {
      return [];
    }
  }

  /** @returns {Record<string, unknown>} The emitted per-SKU variant prices; empty on anything
   * unreadable, which reads as nothing sellable - the same answer checkout gives to a gone SKU. */
  #parsePrices() {
    const script = this.querySelector('script[data-customizer-chain-prices]');
    if (!script?.textContent) return {};
    try {
      const parsed = JSON.parse(script.textContent);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  /** Logs once for QA: which list this product shows (every product reads the shared add-on
   * collection), and a warning when that list renders empty. */
  #reportGroupScope() {
    const scope = this.dataset.groupScope ?? '';
    const group = this.dataset.group ?? '';
    const collection = this.dataset.collection ?? 'add-on-chains';
    const truncated = this.dataset.truncated ?? '';
    if (truncated) {
      console.warn(
        `[customizer] the '${collection}' collection holds ${truncated} products but the chain graph reads at most 50 - chains beyond that are not offered.`
      );
    }
    if (this.#categories.length === 0) {
      console.warn(
        `[customizer] the chain step renders empty: no published, available chain in the '${collection}' collection (scope: ${scope || 'strict'}). Publishing the add-on chain products is what puts them on sale.`
      );
      return;
    }
    if (!scope || scope === 'strict') return;
    const named = group ? `"${group}"` : 'an unnamed group';
    console.info(
      `[customizer] chain group ${named} shows the shared '${collection}' list (${this.#categories.length} audiences); per-product curation is the document's chain.categories.`
    );
  }

  /** Logs once when the product's document narrows the audiences, so an audience missing
   * from this product reads as the merchant's choice rather than a seeding gap. */
  #reportAudienceLimit() {
    const only = this.dataset.only ?? '';
    if (!only) return;
    console.info(
      `[customizer] this product offers ${this.#categories.length} chain audience(s); its document limits them to: ${only}.`
    );
  }

  // ---- handlers (leading-slash `on:` bindings target this component) ----

  /** @param {Event} event */
  openCategory(event) {
    const index = this.#indexOf(event);
    if (index < 0 || !this.#categories[index]) return;
    this.#categoryIndex = index;
    this.open('options');
  }

  /** @param {Event} event */
  openOption(event) {
    const index = this.#indexOf(event);
    const category = this.#categories[this.#categoryIndex];
    if (index < 0 || !category?.options[index]) return;
    this.#optionIndex = index;
    this.open('lengths');
  }

  /**
   * Pick a length: this is the terminal choice, so commit the full chain (category + style +
   * metal + length) to the store. The length carries the whole chain price (`lengthPriceCents`);
   * a style has no price of its own, so `optionPriceCents` is always 0.
   * @param {Event} event
   */
  pickLength(event) {
    const index = this.#indexOf(event);
    const category = this.#categories[this.#categoryIndex];
    const option = category?.options[this.#optionIndex];
    const length = option?.lengths[index];
    if (!category || !option || !length) return;

    /** @type {ChainSelection} */
    const selection = {
      // The chain variant's SKU: the stable id the pricing function resolves through
      // `custom.customizer_components.chains`. Everything else here is display copy.
      key: length.key || '',
      category: category.title,
      option: option.title,
      length: length.label,
      metal: length.metal || '',
      sku: length.sku || '',
      variantId: Number.isSafeInteger(length.variantId) ? length.variantId : undefined,
      optionPriceCents: 0,
      lengthPriceCents: length.priceCents || 0,
      // The row's OWN metal shot, never the style card's donor: one card spans every metal, so the
      // donor sells one of them and a row that borrowed it advertised the wrong finish.
      image: length.image ?? null,
      adjustable: Boolean(length.adjustable),
    };
    const before = this.#selection;
    const choiceValue = selection.sku || selection.key || null;
    const changed = (before ? before.sku || before.key || null : undefined) !== choiceValue;
    this.#selection = selection;
    // `#selection` is set BEFORE setChain, so the echoed store update no-ops in `#onStoreUpdate`
    // and the single repaint below is the only one (lights up the picked row, updates the crumb).
    if (this.#store?.setChain(selection) && changed) {
      track(this, 'choice', { choice: 'chain', choice_value: choiceValue });
    }
    this.refreshView();
  }

  /** No schema control key for the chain, so the confirm round trip keys on this synthetic one,
   * underscore-prefixed so it never collides with a generator-produced control key. */
  static #REMOVE_KEY = '_chain_selection';

  /** The breadcrumb REMOVE pill asks the shared confirm dialog first, same as every other Remove
   * in the wizard; the clear happens in the `customizer:remove-confirmed` listener. */
  requestRemove() {
    this.dispatchEvent(
      new CustomizerConfirmRemoveEvent(
        this.#configId,
        CustomizerChain.#REMOVE_KEY,
        this.dataset.removeBody ?? null
      )
    );
  }

  /**
   * The confirm dialog approved the removal: clear the committed chain but STAY on this view, since
   * the shopper is mid-browse and just unpicked. Only an external clear (Skip) resets the
   * drill to the audience list. `#selection` is nulled BEFORE setChain so the echoed store update
   * no-ops in `#onStoreUpdate`, the same discipline as {@link pickLength}.
   * @param {Event} event
   */
  #onRemoveConfirmed = (event) => {
    const { configId, controlKey } = /** @type {CustomEvent} */ (event).detail;
    if (configId !== this.#configId || controlKey !== CustomizerChain.#REMOVE_KEY) return;
    this.#selection = null;
    this.#store?.setChain(null);
    this.refreshView();
  };

  /**
   * The `data-index` of the clicked control. `event.target` is the bound element (component.js
   * proxies it), so `.closest` resolves the index even when the click lands on a child.
   * @param {Event} event
   * @returns {number} The index, or -1.
   */
  #indexOf(event) {
    const el = /** @type {Element} */ (event.target).closest('[data-index]');
    if (!(el instanceof HTMLElement)) return -1;
    const index = Number(el.dataset.index);
    return Number.isInteger(index) ? index : -1;
  }

  /**
   * @param {string} view
   * @returns {Node}
   */
  renderView(view) {
    if (view === 'lengths') return this.#renderLengths();
    if (view === 'options') return this.#renderOptions();
    return this.#renderCategories();
  }

  /** Push the current view's chain image to the store so the hero preview follows the drill. */
  viewDidRender() {
    this.#store?.setChainPreviewImage(this.#viewImage());
  }

  /**
   * The chain image for the CURRENT view, what the hero shows as the shopper drills: a loose-chain
   * shot through gender/style, then the PICKED row's own shot on the size view, which is how
   * choosing a metal changes the picture. A picked row with no shot of its own stays null rather
   * than borrowing a donor, and before a pick the style answers in the locket's own tone.
   * @returns {string | null}
   */
  #viewImage() {
    const category = this.#categories[this.#categoryIndex];
    const option = category?.options[this.#optionIndex];

    if (this.view === 'lengths' && category && option) {
      // The picked length's image, only when the committed selection is THIS style; otherwise the
      // shopper is browsing a fresh style's lengths and no size applies yet.
      const picked =
        this.#selection?.category === category.title && this.#selection?.option === option.title
          ? option.lengths.find(
              (length) =>
                length.label === this.#selection?.length &&
                (length.metal ?? '') === (this.#selection?.metal ?? '')
            )
          : undefined;
      if (picked) return picked.image;
      return this.#optionImage(option) ?? category.image ?? this.#entryImage();
    }

    if (this.view === 'options' && category) {
      const shown = category.options.filter((opt) => this.#optionVisible(opt));
      const first = shown.map((opt) => this.#optionImage(opt)).find(Boolean);
      return category.image ?? first ?? this.#entryImage();
    }

    return this.#entryImage();
  }

  /**
   * The chain image shown on entry (the category list) and the last-ditch fallback for every view:
   * the add-on collection's own image when the merchant set one, else the first category
   * image, else - once the locket's tone is known and stocked - the first style the tone filter
   * leaves, answered the way its card is (its in-tone row, else that style's own donor), else any
   * style's donor, else null (which lets the hero fall back to the committed selection's image,
   * then the locket composite).
   * @returns {string | null}
   */
  #entryImage() {
    if (this.dataset.collectionImage) return this.dataset.collectionImage;
    for (const category of this.#categories) {
      if (category.image) return category.image;
    }
    // Only where the filter narrows: with no tone, or in an audience with nothing in it, every row
    // is visible and this would swap a style's own card art for whichever metal sorts first.
    const tone = this.#tone;
    if (tone) {
      // Ask each surviving style for its in-tone row, the picture its card already shows. The first
      // image in the graph could show a finish the locket cannot be built in.
      for (const category of this.#categories) {
        if (!this.#audienceTones.get(category.title)?.has(tone)) continue;
        for (const option of category.options) {
          if (!this.#optionVisible(option)) continue;
          const image = this.#optionImage(option);
          if (image) return image;
        }
      }
    }
    for (const category of this.#categories) {
      for (const option of category.options) {
        if (option.image) return option.image;
      }
    }
    return null;
  }

  /** @returns {HTMLElement} The category (audience) list. */
  #renderCategories() {
    const wrap = this.el('div', 'custom-customizer-chain__categories');

    // No audience emitted a live style, for any reason snippets/custom.customizer-chain.liquid's
    // doc lists: the loop no-ops and only the Skip renders, so the step degrades gracefully.
    const rows = this.el('div', 'custom-customizer-chain__rows');
    this.#categories.forEach((category, index) => {
      // Iterate with the ORIGINAL index so `data-index` still maps back after a row is left out.
      if (!category.options.some((option) => this.#optionVisible(option))) return;

      const row = this.actionButton('custom-customizer-chain__row', '/openCategory');
      row.dataset.index = String(index);
      const chosen = this.#selection?.category === category.title;
      // aria-checked is the shared card styling hook; a plain button exposes it to nothing, so the
      // committed row says so through aria-current.
      row.setAttribute('aria-checked', String(chosen));
      if (chosen) row.setAttribute('aria-current', 'true');
      row.toggleAttribute('data-option-card', true);
      const heading = this.el('span', 'custom-customizer-chain__row-heading');
      const title = this.el('span', 'custom-customizer-chain__row-title');
      title.textContent = category.title;
      heading.append(title);

      if (category.subtitle) {
        const subtitle = this.el('span', 'custom-customizer-chain__row-subtitle');
        subtitle.textContent = category.subtitle;
        heading.append(subtitle);
      }
      row.append(heading);
      const fromLabel = this.#categoryFromLabel(category);
      if (fromLabel) {
        const price = this.el('span', 'custom-customizer-chain__price');
        if (this.dataset.labelFrom) {
          const label = this.el('span', 'custom-customizer-chain__price-label');
          label.textContent = this.dataset.labelFrom;
          price.append(label);
        }
        const amount = this.el('span', 'custom-customizer-chain__price-value');
        amount.textContent = fromLabel;
        price.append(amount);
        row.append(price);
      }
      rows.append(row);
    });
    if (rows.childElementCount > 0) wrap.append(rows);

    wrap.append(this.#skip());
    return wrap;
  }

  /**
   * The audience-view "No, Thank You" skip chip: label plus a skip-forward double-chevron, matching
   * the shared step chip's look but left-aligned below the rows. Routes to the host store's `skip`
   * (`custom-customizer/skip`): advance past the optional chain step, clearing any partial pick.
   * @returns {HTMLButtonElement}
   */
  #skip() {
    const skip = this.actionButton('custom-customizer-chain__skip', 'custom-customizer/skip');
    skip.append(document.createTextNode(this.dataset.labelSkip ?? ''));
    skip.append(this.svgIcon(this.#skipIcon));
    return skip;
  }

  /** @returns {HTMLElement} The style (option) image cards for the active category. */
  #renderOptions() {
    const wrap = this.el('div', 'custom-customizer-chain__options');
    const category = this.#categories[this.#categoryIndex];
    if (!category) return wrap;

    // The trail carries the committed style only when it belongs to THIS audience; the REMOVE pill
    // shows whenever a chain is committed, because clearing is global.
    const committedHere = this.#selection?.category === category.title ? this.#selection : null;
    const trail = committedHere ? `${category.title}, ${committedHere.option}` : category.title;
    wrap.append(
      this.drillHeader(this.dataset.labelSelectStyle ?? '', trail, {
        remove: this.#selection ? '/requestRemove' : null,
      })
    );

    const cards = this.el('div', 'custom-customizer-chain__cards');
    category.options.forEach((option, index) => {
      if (!this.#optionVisible(option)) return;

      // The whole card is the visual frame, so the button itself carries the `image` flavor: border
      // around everything, check bubble inset in the image's badge row.
      const card = this.actionButton('custom-customizer-chain__card', '/openOption');
      card.dataset.index = String(index);
      card.setAttribute('data-option-card', 'image');
      const committed = committedHere?.option === option.title;
      card.setAttribute('aria-checked', String(committed));
      if (committed) card.setAttribute('aria-current', 'true');

      const figure = this.el('span', 'custom-customizer-chain__card-figure');
      const cardImage = this.#optionImage(option);
      if (cardImage) {
        const img = document.createElement('img');
        img.src = cardImage;
        img.alt = '';
        img.loading = 'lazy';
        figure.append(img);
      }
      const adjustableAvailable =
        option.adjustable || option.lengths.some((length) => length.adjustable);
      if (adjustableAvailable && this.dataset.labelAdjustable) {
        figure.append(this.#badge(this.dataset.labelAdjustable));
      }
      card.append(figure);

      const label = this.el('span', 'custom-customizer-chain__card-label');
      const title = this.el('span', 'custom-customizer-chain__card-title');
      title.textContent = option.title;
      label.append(title);

      if (option.description) {
        const desc = this.el('span', 'custom-customizer-chain__card-desc');
        desc.textContent = option.description;
        label.append(desc);
      }
      card.append(label);
      cards.append(card);
    });
    wrap.append(cards);
    return wrap;
  }

  /**
   * The length (size) cards for the active style: compact tiles in a scroll row, the same card
   * language as the metal/size selector, with the size+metal label over the price.
   * @returns {HTMLElement}
   */
  #renderLengths() {
    const wrap = this.el('div', 'custom-customizer-chain__lengths');
    const category = this.#categories[this.#categoryIndex];
    const option = category?.options[this.#optionIndex];
    if (!category || !option) return wrap;

    // Append the picked size and metal to the crumb only when it is committed for THIS style, so
    // browsing another style's sizes never shows a stale one. REMOVE shows whenever a chain is set.
    let trail = `${category.title}, ${option.title}`;
    if (
      this.#selection?.category === category.title &&
      this.#selection?.option === option.title &&
      this.#selection?.length
    ) {
      const sel = this.#selection;
      trail += `, ${sel.metal ? `${sel.length} ${sel.metal}` : sel.length}`;
    }
    wrap.append(
      this.drillHeader(this.dataset.labelSelectLength ?? '', trail, {
        remove: this.#selection ? '/requestRemove' : null,
      })
    );

    const cards = this.el('div', 'custom-customizer-chain__sizes');
    cards.setAttribute('role', 'radiogroup');
    option.lengths.forEach((length, index) => {
      // Filter to the locket tone; iterate with the ORIGINAL index so `data-index` still maps back
      // to `option.lengths[index]` in pickLength after some cards are hidden.
      if (!this.#lengthVisible(length)) return;

      const card = this.actionButton('custom-customizer-chain__size', '/pickLength');
      card.dataset.index = String(index);
      card.setAttribute('role', 'radio');
      card.setAttribute(
        'aria-checked',
        String(
          this.#selection?.category === category.title &&
            this.#selection?.option === option.title &&
            this.#selection?.length === length.label &&
            (this.#selection?.metal ?? '') === (length.metal ?? '')
        )
      );
      card.setAttribute('data-option-card', 'tile');

      const title = this.el('span', 'custom-customizer-chain__size-label');
      title.textContent = length.metal ? `${length.label} ${length.metal}` : length.label;
      card.append(title);

      if (length.badge) card.append(this.#badge(length.badge));

      const price = this.el('span', 'custom-customizer-chain__size-price');
      // The row shows the chain's whole price, not a "+" surcharge.
      price.textContent = length.priceLabel ?? this.dataset.labelIncluded ?? '';
      card.append(price);

      cards.append(card);
    });
    wrap.append(cards);
    return wrap;
  }

  /**
   * @param {string} text
   * @returns {HTMLElement}
   */
  #badge(text) {
    const badge = this.el('span', 'custom-customizer-chain__badge');
    badge.textContent = text;
    return badge;
  }
}

if (!customElements.get('custom-customizer-chain')) {
  customElements.define('custom-customizer-chain', CustomizerChain);
}
